import {
  Box,
  Card,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { useLocation, useNavigate } from "react-router-dom";
import api from "api/api";

const fieldSx = {
    "& .MuiInputBase-root": {
      display: "flex !important",
      placeItems: "unset !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      width: "100% !important",
    },
    "& .MuiInputBase-input": {
      width: "100% !important",
      textAlign: "left !important",
    },
    "& .MuiInputBase-inputMultiline": {
      width: "100% !important",
      textAlign: "left !important",
      display: "block !important",
    },
    "& textarea": {
      width: "100% !important",
      textAlign: "left !important",
    },
  };

  const numberFieldSx = {
    ...fieldSx,
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  };

  const selectSx = {
    "& .MuiInputBase-root": {
      display: "flex !important",
      placeItems: "unset !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      width: "100% !important",
    },
    "& .MuiSelect-select": {
      textAlign: "left !important",
      width: "100% !important",
    },
  };

export default function TierForm() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [preview, setPreview] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    key: "",
    description: "",
    display_order: "",
    icon: null,
  });

  const queryParams = new URLSearchParams(location.search);
  const tierId = queryParams.get("tierId");
  const isEditMode = Boolean(tierId);

  // 🔹 Generate key
  const generateKey = (name) =>
    name
      .toLowerCase()
      .trim()
      .replace(/tier/g, "")
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .replace(/^_+|_+$/g, "");

  // 🔹 Fetch tier details
  const getTierDetails = async () => {
    if (!tierId) return;

    try {
      setIsLoading(true);
      const res = await api.getTierDetails({ tierId });

      if (res.status === 1) {
        const data = res.data;

        setFormData({
          name: data?.name || "",
          key: data?.key || "",
          description: data?.description || "",
          display_order: data?.display_order || "",
          icon: null,
        });

        setPreview(data?.icon || null); // existing image URL
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tierId) getTierDetails();
  }, [tierId]);

  // 🔹 Handle change
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name === "name") {
        return {
          ...prev,
          name: value,
          key: generateKey(value),
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
        alert("Max 2MB image allowed");
        return;
    }

    setFormData((prev) => ({
        ...prev,
        icon: file,
    }));

    setPreview(URL.createObjectURL(file));

    // ✅ allow same file re-upload
    e.target.value = null;
  };

  const removeImage = () => {
    setPreview(null);
    setFormData((prev) => ({
      ...prev,
      icon: null,
    }));
  };

  useEffect(() => {
        return () => {
            if (preview && preview.startsWith("blob:")) {
                URL.revokeObjectURL(preview);
            }
        };
    }, [preview]);

  // 🔹 Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
        setIsSubmitting(true);

        const payload = {
            name: formData.name,
            key: formData.key,
            description: formData.description,
            display_order: Number(formData.display_order),
            // icon: formData.icon, // file object (handled in API)
        };

        if (formData.icon) {
            payload.icon = formData.icon;
        }

        let res;

        if (isEditMode) {
            res = await api.updateTier({
                tierId,
                ...payload,
            });
        } else {
            res = await api.createTier(payload);
        }

        if (res.status === 1) {
            navigate("/tiers");
        } else {
            alert(res.message);
        }
    } catch (err) {
        console.error(err);
        alert("Something went wrong");
    } finally {
        setIsSubmitting(false);
    }
  };

  // 🔹 Loader
  if (isLoading) {
    return (
      <SoftBox display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </SoftBox>
    );
  }

  return (
    <SoftBox py={3} display="flex" justifyContent="center">
      <SoftBox width="100%" maxWidth="850px">
        <Card sx={{ border: "1px solid #f0f2f5" }}>
          <SoftBox p={3}>
            
            {/* HEADER */}
            <SoftBox mb={4}>
              <SoftTypography variant="h6" fontWeight="bold">
                Tier Configuration
              </SoftTypography>
              <SoftTypography variant="caption" color="text">
                Create and manage tier visuals and hierarchy.
              </SoftTypography>
            </SoftBox>

            <form onSubmit={handleSubmit}>

              {/* NAME */}
              <Box mb={3}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Name
                </SoftTypography>
                <TextField
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Gold Tier"
                  sx={fieldSx}
                />
              </Box>

              {/* KEY */}
              <Box mb={3}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Key
                </SoftTypography>
                <TextField fullWidth value={formData.key} disabled />
              </Box>

              {/* DESCRIPTION */}
              <Box mb={3}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Description
                </SoftTypography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  sx={fieldSx}
                />
              </Box>

              {/* DISPLAY ORDER */}
              <Box mb={3}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Display Order
                </SoftTypography>
                <TextField
                  fullWidth
                  type="number"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleChange}
                  sx={numberFieldSx}
                />
              </Box>

              {/* IMAGE UPLOAD */}
              <Box mb={4}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Tier Icon
                </SoftTypography>

                <Box
                  sx={{
                    border: "1px dashed #ccc",
                    borderRadius: "10px",
                    p: 2,
                    textAlign: "center",
                    backgroundColor: "#f8f9fa",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    document.getElementById("tierIconInput").click()
                  }
                >
                  {preview ? (
                    <img
                      src={preview}
                      alt="preview"
                      style={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <SoftTypography variant="caption">
                      Click to upload icon
                    </SoftTypography>
                  )}
                </Box>

                <input
                  type="file"
                  id="tierIconInput"
                  hidden
                  accept="image/png, image/jpeg"
                  onChange={handleImageChange}
                />

                {preview && (
                  <Box mt={2} display="flex" gap={2} alignItems="center">
                    <SoftButton
                      size="small"
                      color="error"
                      onClick={removeImage}
                    >
                      Remove
                    </SoftButton>
                    {formData.icon && (
                      <SoftTypography variant="caption">
                        {formData.icon.name}
                      </SoftTypography>
                    )}
                  </Box>
                )}
              </Box>

              {/* SUBMIT */}
              <Box display="flex" justifyContent="flex-end">
                <SoftButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <CircularProgress
                      size={18}
                      color="inherit"
                      sx={{ mr: 1 }}
                    />
                  )}
                  {isSubmitting
                    ? isEditMode
                      ? "Updating..."
                      : "Saving..."
                    : isEditMode
                    ? "Update Tier"
                    : "Create Tier"}
                </SoftButton>
              </Box>

            </form>
          </SoftBox>
        </Card>
      </SoftBox>
    </SoftBox>
  );
}